import type { Firestore } from 'firebase/firestore';
import type { SyncState } from '../hooks/use-offline-sync';
import { appId } from '../constants';
import { type DBCollection } from '@/lib/utils/constants-shared';
import { saveToCache } from './cache-utils';
import { insertItemToDB, removeItemFromDB, updateItemToDB } from './db-service';
import { generateUniqueId } from './utils';

type MarkForSync = (collection: DBCollection, id: string, type: SyncState['itemsToSync'][0]['type'], storageFields: string[]) => void;
type ResolveSync = (collection: DBCollection, id: string) => void;

export class DataCacheService<ItemDataType extends ItemData> {
    constructor(private db: Firestore,
        private collection: DBCollection,
        private markForSync: MarkForSync,
        private resolveSync: ResolveSync,
        private data: ItemDataType,
        private setData: (data: ItemDataType) => void,
        private sortItems: (items: ItemDataType['items']) => ItemDataType['items'],
        private storageFields: string[]) {
    }

    setNewItems(items?: ItemDataType['items']) {
        if (items && Array.isArray(items)) {
            this.updateCacheData(this.sortItems(items));
        }
        else {
            this.setData(undefined);
        }
    }

    private updateCacheData(items: ItemDataType['items']) {
        const map = new Map<string, number>();
        items.forEach((item, ndx) => map.set(item.id, ndx));
        saveToCache(appId, this.collection, items);
        this.setData({ items, map } as ItemDataType);
    }

    get(id: string): ItemDataType['items'][0] | undefined {
        if (!this.data) return undefined;
        const { items, map } = this.data;
        return items[map.get(id)];
    }

    async add(item: Omit<ItemDataType['items'][0], 'id'>) {
        if (!this.data) return undefined;
        const newItem = await insertItemToDB(this.db, this.collection, item).catch(() => {
            const id = generateUniqueId();
            this.markForSync(this.collection, id, 'add', this.storageFields);
            return { ...item, id };
        });
        const newItems = [newItem, ...this.data.items].map((item, sortOrder) => {
            return { ...item, sortOrder }
        });
        this.setNewItems(newItems);
        return newItem;
    }

    remove(id: string) {
        if (!this.data) return;
        this.updateCacheData(this.data.items.filter(item => item.id !== id));
        removeItemFromDB(this.db, this.collection, id)
            .then(() => this.resolveSync(this.collection, id))
            .catch(() => this.markForSync(this.collection, id, 'delete', this.storageFields));
    }

    update(partial: Partial<ItemDataType['items'][0]> & Pick<ItemDataType['items'][0], 'id'>) {
        if (!this.data) return;
        const existingItem = this.get(partial.id);
        if (!existingItem) return;

        const updatedItem = { ...existingItem, ...partial };
        const newItems = [...this.data.items];
        newItems[this.data.map.get(partial.id)] = updatedItem;
        this.updateCacheData(newItems);
        updateItemToDB(this.db, this.collection, updatedItem)
            .then(() => this.resolveSync(this.collection, partial.id))
            .catch(() => this.markForSync(this.collection, partial.id, 'edit', this.storageFields));
    }

    updateAll(items: ItemDataType['items']) {
        this.updateCacheData(items);
        items.forEach(item => {
            updateItemToDB(this.db, this.collection, item)
                .then(() => this.resolveSync(this.collection, item.id))
                .catch(() => this.markForSync(this.collection, item.id, 'edit', this.storageFields));
        });
    }
}